// The stack of modals on the right (R-U19, DESIGN.md «Модалки и стопка»): a panel with a gap from the top, right and bottom edges that
// slides in; the next one lies on top and the ones under it step back to the left and shrink, the last three seen. Esc and a click on
// the backdrop close the top one, a click on a card under it comes back to that card. Linked cards and the field settings use it.

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IconButton, cx } from './primitives.tsx';
import s from './modal-stack.module.css';

export interface StackLayer {
  key: string;
  /** The layer's name for a screen reader. */
  label: string;
  content: ReactNode;
  /** `card` — a record, wide; `panel` — a settings list, narrow. */
  size?: 'card' | 'panel';
}

let openStacks = 0;
/** A stack is over the screen: the screen under it leaves Esc to the stack. */
export const stackOpen = () => openStacks > 0;

/** The exit plays this long before the closed layer leaves the page (the `--dur` token and a little more). */
const LEAVE_MS = 220;
/** Layers deeper than this are kept mounted but not seen. */
const SEEN = 3;
/** Esc inside these belongs to them: an edited text, an open menu or a dialog over the stack. */
const OWN_ESC = 'input, textarea, select, [contenteditable="true"], [role=menu], [role=listbox], [role=dialog]:not([data-stack-layer])';

/** `onClose(i)` closes layer `i` and every layer above it. */
export function ModalStack({ layers, onClose }: { layers: StackLayer[]; onClose: (index: number) => void }) {
  const [leaving, setLeaving] = useState<StackLayer[]>([]);
  const last = useRef(new Map<string, StackLayer>());
  const sig = layers.map((l) => l.key).join(',');
  // A stack taken off the page mid-exit leaves no timer behind.
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());
  useEffect(() => () => { for (const t of timers.current) clearTimeout(t); }, []);
  // A closed layer keeps its last content while it leaves, so it slides out instead of vanishing.
  useEffect(() => {
    const keys = new Set(layers.map((l) => l.key));
    const gone = [...last.current.values()].filter((l) => !keys.has(l.key));
    last.current = new Map(layers.map((l) => [l.key, l]));
    if (!gone.length) return;
    setLeaving((x) => [...x.filter((l) => !gone.some((g) => g.key === l.key)), ...gone]);
    const t = setTimeout(() => { timers.current.delete(t); setLeaving((x) => x.filter((l) => !gone.includes(l))); }, LEAVE_MS);
    timers.current.add(t);
  }, [sig]);

  const any = layers.length > 0;
  useEffect(() => { if (!any) return; openStacks++; return () => { openStacks--; }; }, [any]);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const countRef = useRef(layers.length);
  countRef.current = layers.length;
  useEffect(() => {
    if (!any) return;
    const on = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if ((e.target as HTMLElement | null)?.closest?.(OWN_ESC) || document.querySelector('[role=dialog][data-state=open]:not([data-stack-layer]), [role=menu]')) return;
      e.preventDefault();
      closeRef.current(countRef.current - 1);
    };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, [any]);

  // The top layer takes the focus when it comes, so Tab and a screen reader start in it.
  const topRef = useRef<HTMLElement>(null);
  useEffect(() => { if (any && !topRef.current?.contains(document.activeElement)) topRef.current?.focus({ preventScroll: true }); }, [sig]);

  if (!any && !leaving.length) return null;
  const keys = new Set(layers.map((l) => l.key));
  const out = leaving.filter((l) => !keys.has(l.key));
  return createPortal(
    <div className={s.root} data-modal-stack="">
      <div className={cx(s.backdrop, !any && s.backdropOut)} onClick={() => any && onClose(layers.length - 1)} aria-hidden />
      {/* One array for the live and the leaving layers: a layer that closes keeps its element and plays its exit. */}
      {[
        ...layers.map((l, i) => {
          const depth = layers.length - 1 - i;
          return (
            <section key={l.key} ref={depth === 0 ? topRef : undefined} role="dialog" aria-modal={depth === 0} aria-label={l.label} data-stack-layer="" tabIndex={-1}
              className={cx(s.layer, s[l.size ?? 'card'], depth > 0 && s.under, depth >= SEEN && s.deep)} style={{ '--depth': Math.min(depth, SEEN) } as CSSProperties}>
              <div className={s.body} inert={depth > 0}>{l.content}</div>
              {depth > 0 && depth < SEEN && <button type="button" className={s.shield} aria-label={`Вернуться: ${l.label}`} onClick={() => onClose(i + 1)} />}
            </section>
          );
        }),
        ...out.map((l) => (
          <section key={l.key} aria-hidden className={cx(s.layer, s[l.size ?? 'card'], s.leaving)} style={{ '--depth': 0 } as CSSProperties}>
            <div className={s.body} inert>{l.content}</div>
          </section>
        )),
      ]}
    </div>,
    document.body,
  );
}

/**
 * The frame of a layer that is not a record (a person, a role, a taxonomy — R-U27): its name and a line under it, the close button,
 * then the body that scrolls; the actions of the layer stay at the bottom. A record's card has its own head (RecordView).
 */
export function StackPane({ title, sub, onClose, footer, children }: { title: ReactNode; sub?: ReactNode; onClose: () => void; footer?: ReactNode; children: ReactNode }) {
  return (
    <div className={s.pane}>
      <header className={s.paneHead}>
        <div className={s.paneTitle}>{title}{sub && <div className={s.paneSub}>{sub}</div>}</div>
        <IconButton icon="close" label="Закрыть" onClick={onClose} />
      </header>
      <div className={s.paneBody}>{children}</div>
      {footer && <footer className={s.paneFoot}>{footer}</footer>}
    </div>
  );
}
